import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import {
  AlignButtons,
  CloseIconStyled,
  Flex,
  MainContainer,
  SecondaryContainer,
  TitleContainer,
  paperStyles,
} from 'v3/shared/components/Modal/EditStoreNameAndGroupModal/styles';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { object, string } from 'yup';
import * as api from 'v3/services/api';
import { useStoreSelector } from 'v3/data/store';
import { setFilterRowsPerPage } from 'v3/data/store/group/actions';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { SelectFormController } from 'v3/shared/components/Inputs/SelectFormController';
import { IData } from 'v3/shared/components/Inputs/SelectFormController/types';
import { IStoresListItem } from 'v3/data/store/stores/types';
import {
  IEditStoreNameAndGroupData,
  IEditStoreNameAndGroupModalProps,
} from 'v3/shared/components/Modal/EditStoreNameAndGroupModal/types';
import { MAX_NAME_CHARACTERS } from 'common/constants';

const schema = object({
  name: string()
    .required('Inserir um nome é obrigatório')
    .max(MAX_NAME_CHARACTERS, `O nome deve ter no máximo ${MAX_NAME_CHARACTERS} caracteres`)
    .trim(),
  storeGroupId: string().required('Selecionar um grupo é obrigatório').trim(),
});

export const EditStoreNameAndGroupModal: React.FC<IEditStoreNameAndGroupModalProps> = ({
  title,
  active,
  onClose,
  currentStore,
  onSave,
}) => {
  const { filter: groupsFilter } = useStoreSelector((store) => store.group);
  const [groups, setGroups] = useState<IData[]>([]);

  const getGroupList = useCallback(async () => {
    try {
      const response = await api.getGroupList(groupsFilter);
      setFilterRowsPerPage(response.data.count);
      let groupList = response.data.items.map((group) => ({
        key: group.name,
        value: group.id,
      }));
      const currentGroupId = currentStore?.storeGroup?.id;
      const currentGroupName = currentStore?.storeGroup?.name;

      const isGroupMissing = currentGroupId && !groupList.find((g) => g.value === currentGroupId);
      if (isGroupMissing) {
        groupList = [{ key: currentGroupName, value: currentGroupId }, ...groupList];
      }

      setGroups(groupList);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de Grupos', error);
    }
  }, [groupsFilter, currentStore]);

  useEffect(() => {
    getGroupList();
  }, [groupsFilter]);

  const {
    control,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<IEditStoreNameAndGroupData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      storeGroupId: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({
      name: currentStore.name ?? '',
      storeGroupId: currentStore?.storeGroup?.id ?? '',
    });
    trigger();
  }, [groups, currentStore, reset, trigger]);

  const handleSave = () => {
    const newNameAndGroup = getValues();
    onSave(newNameAndGroup);
    onClose();
  };

  return (
    <Dialog
      onClose={onClose}
      open={active}
      aria-labelledby="customized-dialog-title"
      aria-describedby="customized-dialog-description"
      PaperProps={paperStyles}
    >
      <MainContainer>
        <TitleContainer>
          <SecondaryContainer>
            <SubtitleLarge>{title}</SubtitleLarge>
          </SecondaryContainer>
          <CloseIconStyled onClick={onClose} />
        </TitleContainer>
        <Flex>
          <InputFormController
            data-testid="input-store-name"
            formControl={control}
            formControlName="name"
            label="Nome da loja"
          />
          <SelectFormController
            data-testid="input-group"
            formControl={control}
            formControlName="storeGroupId"
            label="Grupo"
            setValue={setValue}
            defaultValues={groups}
            useDefaultMapping={false}
          />
        </Flex>
        <AlignButtons>
          <TextButton text="Cancelar" $outline size="small" handleClick={onClose} />
          <FlexButton text="Salvar alterações" size="small" disabled={!isValid} handleClick={handleSave} />
        </AlignButtons>
      </MainContainer>
    </Dialog>
  );
};
